import { useEffect, useRef, useState } from 'react';

import { prefereMenosMovimento } from '@/hooks/use-revelar-ao-rolar';
import { apiBaseUrl } from '@/services/http-client';

/** Quanto da distância restante a miniatura percorre a cada quadro. */
const SUAVIDADE = 0.13;

/**
 * Miniatura que acompanha o cursor sobre a lista de ambientes.
 *
 * Vem do protótipo (`js/main.js`), e é o efeito de assinatura da seção. Três
 * pontos que valem registro:
 *
 * **A posição não passa por estado do React.** Ela muda a cada quadro; um
 * `setState` a 60 por segundo faria a árvore inteira renderizar de novo à toa.
 * O laço escreve direto no `style.transform` do elemento, que é o que o
 * protótipo já fazia — só o *qual* foto mostrar é estado.
 *
 * **Só existe onde há cursor de verdade.** Em tela de toque não há hover, e o
 * `@media (hover: none)` do protótipo virou uma consulta lida uma vez. Também
 * não monta para quem pediu menos movimento.
 *
 * **A consulta é lida na inicialização do estado, não num efeito.** Assim o
 * primeiro render já sabe se deve existir, em vez de aparecer e sumir.
 */
export function useMiniaturaQueSegue() {
  const [ativa] = useState(
    () =>
      typeof window !== 'undefined' &&
      !prefereMenosMovimento() &&
      !window.matchMedia('(hover: none)').matches,
  );

  const [fotoVisivel, setFotoVisivel] = useState<string | null>(null);

  const bolha = useRef<HTMLDivElement>(null);
  const alvo = useRef({ x: 0, y: 0 });
  const atual = useRef({ x: 0, y: 0 });
  const seguindo = useRef(false);
  const quadro = useRef(0);

  // Um laço solto continuaria pedindo quadros depois que o componente sair.
  useEffect(() => () => cancelAnimationFrame(quadro.current), []);

  function passo() {
    atual.current.x += (alvo.current.x - atual.current.x) * SUAVIDADE;
    atual.current.y += (alvo.current.y - atual.current.y) * SUAVIDADE;

    if (bolha.current) {
      const { x, y } = atual.current;

      bolha.current.style.transform = `translate(${String(x)}px, ${String(y)}px) translate(-50%, -50%)`;
    }

    if (seguindo.current) quadro.current = requestAnimationFrame(passo);
  }

  function aoMover(evento: React.PointerEvent) {
    if (!ativa) return;

    alvo.current = { x: evento.clientX, y: evento.clientY };

    if (!seguindo.current) {
      seguindo.current = true;
      // Começa embaixo do cursor, senão a miniatura desliza da origem da tela.
      atual.current = { ...alvo.current };
      quadro.current = requestAnimationFrame(passo);
    }
  }

  function aoSair() {
    seguindo.current = false;
    cancelAnimationFrame(quadro.current);
    setFotoVisivel(null);
  }

  function aoEntrarEm(foto: string | null) {
    if (ativa) setFotoVisivel(foto);
  }

  return { ativa, fotoVisivel, bolha, aoMover, aoSair, aoEntrarEm };
}

/** O quadro flutuante em si. */
export function Miniatura({
  refDaBolha,
  mediaId,
}: {
  refDaBolha: React.RefObject<HTMLDivElement | null>;
  mediaId: string | null;
}) {
  return (
    <div
      ref={refDaBolha}
      aria-hidden="true"
      className={`pointer-events-none fixed top-0 left-0 z-[5] aspect-[4/5] w-[min(24vw,300px)] overflow-hidden transition-opacity duration-[400ms] ease-plane ${
        mediaId === null ? 'opacity-0' : 'opacity-100'
      }`}
    >
      {mediaId !== null && (
        <img
          src={`${apiBaseUrl}/api/v1/media/${mediaId}`}
          alt=""
          className="h-full w-full object-cover"
        />
      )}
    </div>
  );
}
